import { useState, useEffect, useCallback } from 'preact/hooks';
import { useStore } from '../lib/store';
import { npubEncode, nsecEncode } from 'nostr-tools/nip19';
import { hexToBytes } from '../lib/crypto';
import { users as usersApi } from '../lib/api';
import type { MeResponse } from '../lib/types';

export function Settings() {
  const { session, proofs, showToast } = useStore();
  const [keyRevealed, setKeyRevealed] = useState(false);
  const [confirmReveal, setConfirmReveal] = useState(false);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [nip17Saving, setNip17Saving] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);

  const domain = useDomainFromStore();

  useEffect(() => {
    if (!session) return;
    usersApi.me().then(setMe).catch(() => { /* not logged in or fetch failed */ });
  }, [session]);

  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setPushEnabled(!!sub))
        .catch(() => {});
    }
  }, []);

  const toggleNip17 = useCallback(async (next: boolean) => {
    setNip17Saving(true);
    try {
      const updated = await usersApi.update({ nip17Notify: next });
      setMe(updated);
      showToast(next ? 'Nostr DM notifications enabled' : 'Nostr DM notifications disabled', 'success');
    } catch (err) {
      showToast(`Failed to update: ${(err as Error).message}`, 'error');
    } finally {
      setNip17Saving(false);
    }
  }, [showToast]);

  const enablePush = useCallback(async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { showToast('Notification permission denied', 'error'); return; }
      const reg = await navigator.serviceWorker.ready;
      const { publicKey } = await fetch('/api/push/vapid-key').then((r) => r.json());
      if (!publicKey) { showToast('Push not configured', 'error'); return; }
      const raw = atob(publicKey.replace(/-/g, '+').replace(/_/g, '/'));
      const keyBytes = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) keyBytes[i] = raw.charCodeAt(i);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes });
      const s = sub.toJSON();
      await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ endpoint: s.endpoint, keys: s.keys }),
      });
      setPushEnabled(true);
      showToast('Push notifications enabled', 'success');
    } catch (err) {
      showToast(`Failed: ${(err as Error).message}`, 'error');
    }
  }, [showToast]);

  const handleRevealKey = useCallback(() => {
    setKeyRevealed(true);
  }, []);

  const handleCopyKey = useCallback(() => {
    if (!session?.secretKeyHex) return;
    try {
      const nsec = nsecEncode(hexToBytes(session.secretKeyHex));
      navigator.clipboard.writeText(nsec).then(() => {
        showToast('Private key copied! Store it safely.', 'success');
      });
    } catch {
      showToast('Error encoding key', 'error');
    }
  }, [session, showToast]);

  if (!session) return null;

  const npub = (() => {
    try {
      return npubEncode(session.pubkey);
    } catch {
      return '-';
    }
  })();

  return (
    <div class="settings-panel" id="settings-panel" data-testid="settings-view">
      <h2>Settings</h2>

      <div class="settings-section">
        <h3>Identity</h3>
        <div class="detail-row">
          <span class="detail-label">Pubkey</span>
          <span class="detail-value" data-testid="pubkey-display">{session.pubkey.substring(0, 16)}...</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">npub</span>
          <span class="detail-value">{npub.substring(0, 20)}...</span>
        </div>
      </div>

      <div class="settings-section">
        <h3>Key Management</h3>
        <p style="font-size:13px;color:var(--muted);margin-bottom:12px">
          Your Nostr key is stored securely in your browser or extension. Keep it safe — there is no password reset.
        </p>
        <details>
          <summary style="cursor:pointer;color:var(--accent);font-size:13px;font-weight:600">
            Show technical details
          </summary>
          <div style="margin-top:12px;padding:12px;background:var(--bg);border:1px solid var(--border);border-radius:var(--radius)">
            <div class="detail-row">
              <span class="detail-label">Auth method</span>
              <span class="detail-value" data-testid="auth-method-display">
                {session.authMethod === 'nsec' ? 'nsec (local)' : 'NIP-07 extension'}
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Session</span>
              <span class="detail-value">HttpOnly cookie</span>
            </div>
          </div>
        </details>
      </div>

      <div class="settings-section">
        <h3>Key Export</h3>
        <p style="font-size:13px;color:var(--muted);margin-bottom:12px">
          Export your private key for backup. Handle with extreme care — anyone with this key has full access to your email.
        </p>
        <div>
          {!session.secretKeyHex ? (
            <p style="font-size:13px;color:var(--muted)">
              Key export is only available when signed in with nsec. If you signed in with a Nostr extension, your key is managed by the extension.
            </p>
          ) : !keyRevealed ? (
            confirmReveal ? (
              <div class="confirm-reveal">
                <p style="font-size:13px;color:var(--warning);margin-bottom:8px">
                  Anyone with this key has full access to your email. Reveal it only on a trusted device.
                </p>
                <div style="display:flex;gap:8px">
                  <button class="btn btn-ghost btn-sm" onClick={() => setConfirmReveal(false)}>Cancel</button>
                  <button class="btn btn-sm" style="background:var(--error);color:#fff" onClick={() => { setKeyRevealed(true); setConfirmReveal(false); }} data-testid="confirm-reveal-btn">
                    Yes, Reveal Key
                  </button>
                </div>
              </div>
            ) : (
              <button class="btn btn-ghost btn-sm" onClick={() => setConfirmReveal(true)} data-testid="reveal-key-btn">
                Reveal Private Key
              </button>
            )
          ) : (
            <>
              <div class="nsec-warning">
                <strong>Your private key (nsec)</strong>
                <p>This is your only key. Copy it somewhere safe. Never share it with anyone.</p>
              </div>
              <div class="key-display">
                <code>{nsecEncode(hexToBytes(session.secretKeyHex))}</code>
              </div>
              <div class="key-actions">
                <button class="btn btn-ghost btn-sm" onClick={handleCopyKey}>
                  Copy Key
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div class="settings-section">
        <h3>Nostr DM Notifications</h3>
        <p style="font-size:13px;color:var(--muted);margin-bottom:12px">
          When enabled, nomail sends a gift-wrapped NIP-17 DM to your npub whenever a new email arrives. Read it inside any Nostr client (Damus, Amethyst, Iris). Content is end-to-end encrypted; only the relay sees that you received a DM. Default relay: <code>wss://relay.cashu.email</code>.
        </p>
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer">
          <input
            type="checkbox"
            checked={me?.nip17_notify ?? false}
            disabled={!me || nip17Saving}
            onChange={(e) => toggleNip17((e.target as HTMLInputElement).checked)}
            data-testid="nip17-notify-toggle"
          />
          <span>{nip17Saving ? 'Saving...' : me?.nip17_notify ? 'Enabled' : 'Disabled'}</span>
        </label>
      </div>

      <div class="settings-section">
        <h3>Push Notifications</h3>
        <p style="font-size:13px;color:var(--muted);margin-bottom:12px">
          Get a push notification on your device when new email arrives. Works even when the app is closed.
        </p>
        <button class="btn" style="width:auto" onClick={() => void enablePush()} disabled={pushEnabled} data-testid="push-enable-btn">
          {pushEnabled ? 'Enabled' : 'Enable Push Notifications'}
        </button>
      </div>

      <div class="settings-section">
        <h3>Payment History</h3>
        <p style="font-size:13px;color:var(--muted);margin-bottom:8px">
          Cashu proofs held for alias trials.
        </p>
        {proofs.length === 0 ? (
          <p style="font-size:13px;color:var(--muted)">No payment history.</p>
        ) : (
          proofs.map((p, i) => (
            <div key={i} class="addr-card">
              <span class="addr-text">
                {p.localPart || 'alias'}@{p.domain || domain}
              </span>
              <span
                class={
                  'addr-badge ' +
                  (p.status === 'held'
                    ? 'trial'
                    : p.status === 'redeemed'
                      ? 'primary'
                      : '')
                }
              >
                {p.status}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function useDomainFromStore(): string {
  const store = useStore();
  void store;
  const primary = store.addresses.find((a) => a.isPrimary);
  return primary?.domain || '';
}
